import MuiAlert from '@material-ui/lab/Alert';
import Snackbar from '@material-ui/core/Snackbar';
import MuiAlertTitle from '@material-ui/lab/AlertTitle';
import React, { createContext, useReducer, useContext } from 'react';

export enum Severity {
  SUCCESS = 'success',
  ERROR = 'error',
  WARNING = 'warning',
  INFO = 'info'
}

type PushNotification = (params: { title: string; message: string; severity: Severity }) => void;
type State = { id: string; title: string; message: string; show: boolean; severity: Severity }[];
type Action =
  | { type: 'pop'; id: string }
  | { type: 'hide'; id: string }
  | { type: 'push'; id: string; title: string; message: string; severity: Severity };

interface Props {
  children: React.ReactNode | React.ReactNode[];
}

const initialState: State = [];
const NotificationsDispatch = createContext<PushNotification | undefined>(undefined);

const reducer = (state: State = initialState, action: Action): State => {
  switch (action.type) {
    case 'push':
      return [
        ...state,
        { id: action.id, title: action.title, message: action.message, show: true, severity: action.severity }
      ];
    case 'hide':
      const hideIndex = state.findIndex((notification) => notification.id === action.id);
      const modifiedNotification = { ...state[hideIndex], show: false };
      return [...state.slice(0, hideIndex), modifiedNotification, ...state.slice(hideIndex + 1)];
    case 'pop':
      const poppedIndex = state.findIndex((notification) => notification.id === action.id);
      if (poppedIndex === state.length - 1) {
        return state.slice(0, -1);
      }
      if (poppedIndex === 0) {
        return state.slice(1);
      }
      return state.slice(0, poppedIndex).concat(state.slice(poppedIndex + 1));
  }
};

export const usePushNotifications = () => {
  const ctx = useContext(NotificationsDispatch);
  if (ctx === undefined) throw new Error('Notification Services is used out of its scope');
  return ctx;
};

export const NotificationService = ({ children }: Props) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const pushNotification = ({ title, message, severity }: { title: string; message: string; severity: Severity }) => {
    dispatch({ type: 'push', id: String(Date.now()), title, message, severity });
  };

  const onClose = (notificationId: string, reason?: string) => {
    if (reason === 'clickaway') return;
    dispatch({ type: 'hide', id: notificationId });
  };

  const onExited = (notificationId: string) => {
    dispatch({ type: 'pop', id: notificationId });
  };

  return (
    <NotificationsDispatch.Provider value={pushNotification}>
      {state.map((notification) => (
        <Snackbar
          style={{ marginRight: '24px' }}
          key={notification.id}
          open={notification.show}
          onExited={() => onExited(notification.id)}
          onClose={(_, reason) => onClose(notification.id, reason)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <MuiAlert
            onClick={() => onClose(notification.id)}
            severity={notification.severity}
            style={{ whiteSpace: 'break-spaces' }}
            onClose={() => onClose(notification.id)}
            variant={notification.severity === Severity.SUCCESS ? 'filled' : undefined}
          >
            <MuiAlertTitle>{notification.title}</MuiAlertTitle>
            {notification.severity !== Severity.ERROR && notification.message}
            {notification.severity === Severity.ERROR && notification.message}
          </MuiAlert>
        </Snackbar>
      ))}
      {children}
    </NotificationsDispatch.Provider>
  );
};
